import { cn } from "@/lib/utils";

export interface ScorecardProps {
  businessModel: number;
  operatingModel: number;
  protector: number;
  overall: number;
  confidence: number;
  exposureLevel: string;
  strategicPosition: string;
}

function fmt(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}

function exposureToneClasses(level: string): string {
  if (level === "High") {
    return "bg-accent text-accent-foreground";
  }
  if (level === "Medium") {
    return "bg-accent-light/60 text-accent";
  }
  return "bg-primary/10 text-muted-foreground";
}

/**
 * The four-tile score summary shown on the report and expert pages. Overall
 * exposure, confidence, and exposure level always come from the original
 * system result; layer scores may be expert-adjusted (caller decides).
 */
export function Scorecard(props: ScorecardProps) {
  const tiles = [
    { label: "Business model", value: props.businessModel, accent: false },
    { label: "Operating model", value: props.operatingModel, accent: false },
    { label: "Protectors", value: props.protector, accent: false },
    { label: "Overall exposure", value: props.overall, accent: true },
  ];

  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <span
          className={cn(
            "rounded px-2.5 py-1 text-[11px] font-bold tracking-wide uppercase",
            exposureToneClasses(props.exposureLevel),
          )}
        >
          {props.exposureLevel} exposure
        </span>
        <strong className="text-primary text-lg">{props.strategicPosition}</strong>
        <span className="text-muted text-sm">· confidence {fmt(props.confidence)}%</span>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {tiles.map((tile) => (
          <div
            key={tile.label}
            className={cn("rounded-xl border p-4", tile.accent ? "border-accent" : "border-border")}
          >
            <div
              className={cn(
                "text-[40px] leading-tight font-normal",
                tile.accent ? "text-accent" : "text-primary",
              )}
            >
              {fmt(tile.value)}
            </div>
            <div className="text-muted mt-1 text-[11px] font-semibold tracking-wide uppercase">
              {tile.label}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
